import React, { forwardRef, ReactElement } from "react";

import classNames from "classnames";

import {
  StyledBadge,
  StyledCounterButton,
  StyledCounterButtonLabel,
  StyledCounterButtonReset,
  StyledCounterButtonWrapper,
} from "./Styles";
import CloseSmallIcon from "../Icons/collection/CloseSmall";
import { Tooltip } from "../Tooltip/Tooltip";

export interface ICounterButtonProps {
  /** Set active state */
  active?: boolean;
  /** Label for value */
  label?: string;
  /** Icon */
  icon?: ReactElement;
  /** Number of applied filters */
  counter?: number;
  /** OnClearAll callback */
  onClearAll: (event?) => void;
  /** Tooltip content */
  tooltipText?: string;
  /** Applies passed classes */
  className?: string;
  /** Applies passed classes for label text */
  labelClassName?: string;
  /** Disabled state of a button */
  disabled?: boolean;
}

export const CounterButton = forwardRef<HTMLDivElement, ICounterButtonProps>(
  (
    {
      label,
      icon,
      active,
      counter = 0,
      tooltipText,
      onClearAll,
      className,
      labelClassName,
      disabled = false,
      ...args
    },
    ref
  ) => {
    return (
      <StyledCounterButtonWrapper ref={ref} className={className}>
        <StyledCounterButton
          disabled={disabled}
          aria-disabled={disabled}
          className={classNames("c-counter-button", {
            "c-counter-button__selected": counter,
          })}
          {...args}
          variant="text gray"
          $active={counter > 0 || active}
          $selected={counter > 0}
        >
          {icon &&
            React.cloneElement(icon, {
              className: classNames("c-counter-button__icon"),
            })}
          {label && (
            <StyledCounterButtonLabel
              color="secondary"
              weight="medium"
              className={classNames("c-counter-button__label", labelClassName)}
              $active={counter > 0 || active}
            >
              {label}
            </StyledCounterButtonLabel>
          )}
          {counter > 0 ? (
            <StyledBadge isStandalone={true} dimension={16} value={counter} />
          ) : null}
        </StyledCounterButton>
        {counter > 0 ? (
          <Tooltip
            title={`${tooltipText}`}
            popperTooltipClassName="z-index-1300"
          >
            <StyledCounterButtonReset
              variant="text colored"
              className="c-counter-button__reset"
              onClick={onClearAll}
            >
              <CloseSmallIcon />
            </StyledCounterButtonReset>
          </Tooltip>
        ) : null}
      </StyledCounterButtonWrapper>
    );
  }
);

CounterButton.displayName = "CounterButton";
